import { useMemo, useState, useEffect } from "react";
import { useCurrentAccountId } from "@zapier/identity";
import { clientEnv } from "../../lib/env";
import { buttonLogger } from "../utils/logger";
import { isZapierDomain } from "../utils/isZapierDomain";
import type { UrlParameters } from "./useUrlParameters";

export type AuthorizationFailureReason =
  | "authenticated"
  | "not_authenticated"
  | "missing_params"
  | "pending";

export interface AuthorizationErrorDetails {
  message: string;
  helpText: string;
  helpLink: string;
}

export interface AuthorizationResult {
  isAuthorized: boolean;
  isLoading: boolean;
  reason: AuthorizationFailureReason;
  errorDetails: AuthorizationErrorDetails | null;
}

export function getMissingAuthorizationParams(
  urlParams: UrlParameters,
): string[] {
  const missingParams: string[] = [];

  // Non-breaking bug fix: validate against the resolved configId so
  // authorization matches launch behavior when config_id comes from env fallback.
  if (!urlParams.configId) missingParams.push("config_id");
  if (!urlParams.callbackUrl) missingParams.push("redirect_uri");
  if (!urlParams.state) missingParams.push("state");

  return missingParams;
}

/**
 * Hook to validate WABA signup flow authorization.
 *
 * Validates that required OAuth parameters are present. This ensures users
 * are coming through the proper Zapier connection flow, not direct navigation.
 *
 * Required parameters:
 * - configId: Facebook embedded signup configuration ID (critical for SDK)
 * - callbackUrl: OAuth redirect URI for token exchange
 * - state: OAuth state parameter for CSRF protection
 *
 * Authentication is checked but not enforced - middleware handles that layer.
 */
export function useWabaAuthorization(
  urlParams: UrlParameters,
): AuthorizationResult {
  const currentAccountId = useCurrentAccountId();
  const [mounted, setMounted] = useState(false);

  useEffect(() => {
    setMounted(true);
  }, []);

  return useMemo(() => {
    if (!mounted) {
      return {
        isAuthorized: false,
        isLoading: true,
        reason: "pending",
        errorDetails: null,
      };
    }

    const zapierOrigin =
      clientEnv.NEXT_PUBLIC_ZAPIER_ORIGIN || "https://zapier.com";

    // Layer 1: Authentication Check
    // If user is not authenticated, redirect to login with return URL
    // Note: Skip authentication check on non-Zapier domains (e.g., Vercel preview)
    // to prevent false warnings when cookies aren't accessible due to CORS
    if (!currentAccountId && isZapierDomain()) {
      buttonLogger.warn("authorization_failed_not_authenticated", {
        reason: "no_current_account_id",
      });

      // Build login URL with return path
      const currentUrl =
        typeof window !== "undefined" ? window.location.href : "";
      const loginUrl = `${zapierOrigin}/app/login${currentUrl ? `?next=${encodeURIComponent(currentUrl)}` : ""}`;

      return {
        isAuthorized: false,
        isLoading: false,
        reason: "not_authenticated",
        errorDetails: {
          message: "Authentication Required",
          helpText:
            "You must be logged into Zapier to connect a WhatsApp Business Account. You'll be returned to this page after logging in.",
          helpLink: loginUrl,
        },
      };
    }

    // Layer 2: Launch parameter validation
    // Validate against the resolved params so authorization stays consistent
    // with the actual launch behavior and environment fallbacks.
    const missingParams = getMissingAuthorizationParams(urlParams);
    const hasRequiredParams = missingParams.length === 0;

    if (!hasRequiredParams) {
      buttonLogger.warn("authorization_failed_missing_params", {
        missingParams,
        hasState: !!urlParams.state,
        hasCallbackUrl: !!urlParams.callbackUrl,
        hasConfigId: !!urlParams.configId,
      });

      // Build login URL with return path for missing params case too
      const currentUrl =
        typeof window !== "undefined" ? window.location.href : "";
      const loginUrl = `${zapierOrigin}/app/login${currentUrl ? `?next=${encodeURIComponent(currentUrl)}` : ""}`;

      return {
        isAuthorized: false,
        isLoading: false,
        reason: "missing_params",
        errorDetails: {
          message: "Authentication Required",
          helpText:
            "You must be logged into Zapier to connect a WhatsApp Business Account. Please log in and start the connection process from within Zapier.",
          helpLink: loginUrl,
        },
      };
    }

    // All checks passed
    buttonLogger.info("authorization_success", {
      accountId: currentAccountId,
      hasAllRequiredParams: true,
    });

    return {
      isAuthorized: true,
      isLoading: false,
      reason: "authenticated",
      errorDetails: null,
    };
  }, [
    mounted,
    currentAccountId,
    urlParams.state,
    urlParams.callbackUrl,
    urlParams.configId,
  ]);
}
